import { Suspense, FC, lazy } from 'react';

import { Routes, Route } from 'react-router-dom';

import { DashboardProvider } from 'commons/utils/hooks/useDashboardContext';
import { CheckoutSuccessProvider } from 'commons/utils/hooks/useDispatchOnEnoughBalance';
import NexoSyncRoute from 'commons/utils/nexo/nexo-sync-route';

import SaveOnShippingRoute from 'domains/orders/save-on-shipping/routes';
import Loading from 'domains/widgets/loading/loading';

import AppRedirect from './app-redirect';
import AppBlockedGuard from './AppBlockedGuard';
import { ROUTES } from './constants';
import OrdersContextProvider from './ordersProvider';
import PreferencesContextProvider from './preferencesProvider';
import ReadOnlyGuard from './ReadOnlyGuard';
import ShippingDetailsContextProvider from './shippingDetailsProvider';

const Auth = lazy(() => import('domains/auth'));
const Error = lazy(() => import('domains/error'));
const Dashboard = lazy(() => import('domains/orders/dashboard'));
const CheckoutSuccess = lazy(() => import('domains/checkout/success'));
const Configurations = lazy(() => import('domains/configurations'));
const ConfigurationPreferences = lazy(
  () => import('domains/configurations/preferences')
);
const ConfigurationModality = lazy(
  () => import('domains/configurations/pages/carrier')
);
const ConfigurationCarrier = lazy(
  () => import('domains/configurations/pages/carrier/detail')
);
const ShippingDetails = lazy(() => import('domains/shipping-details'));
const Billing = lazy(() => import('domains/billing'));
const BillingDetails = lazy(() => import('domains/billing/post-paid/details'));
const SingleShipment = lazy(
  () => import('domains/single-shipment/single-shipment')
);
const SingleShipmentQuotation = lazy(
  () => import('domains/single-shipment/quotation')
);
const ReverseShipment = lazy(
  () => import('domains/reverse-shipment/reverse-shipment')
);
const ReverseShipmentQuotation = lazy(
  () => import('domains/reverse-shipment/quotation')
);
const AgencyShipment = lazy(() => import('domains/agency-shipment'));
const AgencyShipmentQuotation = lazy(
  () => import('domains/agency-shipment/quotation')
);
const DropoffPoints = lazy(() => import('domains/points'));

const LandingPage = lazy(() => import('domains/landing-page/pages'));
const BalanceStatus = lazy(() => import('domains/balance/status'));
const BalanceNuvemPago = lazy(() => import('domains/balance/nuvem-pago'));

const CreateSingleShipment = lazy(
  () =>
    import(
      'domains/single-shipment/create-single-shipment/create-single-shipment'
    )
);

const CreateSingleShipmentQuotation = lazy(
  () => import('domains/single-shipment/create-single-shipment/quotation')
);
const CreateSingleShipmentCustomer = lazy(
  () => import('domains/single-shipment/create-single-shipment/customer')
);
const CreateSingleShipmentReview = lazy(
  () => import('domains/single-shipment/create-single-shipment/review')
);

const NewReverseShipmentQuotation = lazy(
  () => import('domains/reverse-shipment/new-reverse/quotation')
);

const PublicRoutes: FC = () => (
  <Suspense fallback={<Loading />}>
    <NexoSyncRoute>
      <Routes>
        <Route
          path={ROUTES.ERROR}
          element={
            <Suspense fallback={<Loading />}>
              <Error />
            </Suspense>
          }
        />
        <Route element={<OrdersContextProvider />}>
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <Suspense fallback={<Loading />}>
                <DashboardProvider>
                  <Dashboard />
                </DashboardProvider>
              </Suspense>
            }
          />
          <Route element={<ShippingDetailsContextProvider />}>
            <Route path={ROUTES.SHIPPINGDETAILS}>
              <Route
                path=":path/:cycleId"
                element={
                  <Suspense fallback={<Loading />}>
                    <ShippingDetails />
                  </Suspense>
                }
              />
              <Route
                path=""
                element={
                  <Suspense fallback={<Loading />}>
                    <ShippingDetails />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          <Route element={<ReadOnlyGuard />}>
            <Route element={<AppBlockedGuard />}>
              <Route
                path={ROUTES.SINGLE_SHIPMENT}
                element={
                  <Suspense fallback={<Loading />}>
                    <SingleShipment />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.SINGLE_SHIPMENT_QUOTATION}
                element={
                  <Suspense fallback={<Loading />}>
                    <SingleShipmentQuotation />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.AGENCY_SHIPMENT}
                element={
                  <Suspense fallback={<Loading />}>
                    <AgencyShipment />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.AGENCY_SHIPMENT_QUOTATION}
                element={
                  <Suspense fallback={<Loading />}>
                    <AgencyShipmentQuotation />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.REVERSE_SHIPMENT}
                element={
                  <Suspense fallback={<Loading />}>
                    <ReverseShipment />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.REVERSE_SHIPMENT_QUOTATION}
                element={
                  <Suspense fallback={<Loading />}>
                    <ReverseShipmentQuotation />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.NEW_REVERSE_SHIPMENT_QUOTATION}
                element={
                  <Suspense fallback={<Loading />}>
                    <NewReverseShipmentQuotation />
                  </Suspense>
                }
              />
              <Route path={ROUTES.NEW_SINGLE_SHIPMENT}>
                <Route
                  path="edit"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipment />
                    </Suspense>
                  }
                />
                <Route
                  path=""
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipment />
                    </Suspense>
                  }
                />
              </Route>

              <Route path={ROUTES.NEW_SINGLE_SHIPMENT_QUOTATION}>
                <Route
                  path="edit"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipmentQuotation />
                    </Suspense>
                  }
                />
                <Route
                  path=""
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipmentQuotation />
                    </Suspense>
                  }
                />
              </Route>

              <Route path={ROUTES.NEW_SINGLE_SHIPMENT_CUSTOMER}>
                <Route
                  path="edit"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipmentCustomer />
                    </Suspense>
                  }
                />
                <Route
                  path=""
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateSingleShipmentCustomer />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path={ROUTES.NEW_SINGLE_SHIPMENT_REVIEW}
                element={
                  <Suspense fallback={<Loading />}>
                    <CreateSingleShipmentReview />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          <Route
            path={ROUTES.BALANCE_STATUS}
            element={
              <Suspense fallback={<Loading />}>
                <BalanceStatus />
              </Suspense>
            }
          />

          <Route element={<ReadOnlyGuard />}>
            <Route
              path={ROUTES.BALANCE_NUVEM_PAGO}
              element={
                <Suspense fallback={<Loading />}>
                  <BalanceNuvemPago />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route element={<ReadOnlyGuard />}>
          <Route
            path={ROUTES.CONFIGURATIONS}
            element={
              <Suspense fallback={<Loading />}>
                <Configurations />
              </Suspense>
            }
          />

          <Route element={<PreferencesContextProvider />}>
            <Route
              path={ROUTES.CONFIGURATION_PREFERENCES}
              element={
                <Suspense fallback={<Loading />}>
                  <ConfigurationPreferences />
                </Suspense>
              }
            />
          </Route>

          <Route element={<OrdersContextProvider />}>
            <Route
              path={ROUTES.CONFIGURATION_CARRIER}
              element={
                <Suspense fallback={<Loading />}>
                  <ConfigurationModality />
                </Suspense>
              }
            />
          </Route>

          <Route
            path={ROUTES.CONFIGURATION_CARRIER_NAME}
            element={
              <Suspense fallback={<Loading />}>
                <ConfigurationCarrier />
              </Suspense>
            }
          />
        </Route>

        <Route element={<ReadOnlyGuard />}>
          <Route
            path={ROUTES.BILLING}
            element={
              <Suspense fallback={<Loading />}>
                <Billing />
              </Suspense>
            }
          />

          <Route
            path={ROUTES.BILLINGDETAILS}
            element={
              <Suspense fallback={<Loading />}>
                <BillingDetails />
              </Suspense>
            }
          />
        </Route>

        <Route
          path={ROUTES.DROPOFF_POINTS}
          element={
            <Suspense fallback={<Loading />}>
              <DropoffPoints />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.LANDING_PAGE}
          element={
            <Suspense fallback={<Loading />}>
              <LandingPage />
            </Suspense>
          }
        />

        <Route element={<CheckoutSuccessProvider />}>
          <Route
            path={ROUTES.CHECKOUT_SUCCESS}
            element={
              <Suspense fallback={<Loading />}>
                <CheckoutSuccess />
              </Suspense>
            }
          />
        </Route>

        <Route element={<ReadOnlyGuard />}>{SaveOnShippingRoute}</Route>

        {process.env.REACT_APP_CURRENT_ENVIRONMENT === 'staging' && (
          <Route
            path={ROUTES.AUTH}
            element={
              <Suspense fallback={<Loading />}>
                <Auth />
              </Suspense>
            }
          ></Route>
        )}

        <Route path={ROUTES.HEALTH} element={<div>Ok</div>}></Route>

        <Route path="/" element={<AppRedirect />}></Route>
      </Routes>
    </NexoSyncRoute>
  </Suspense>
);

export default PublicRoutes;
